import React from 'react';
import { DatePicker as AppDatePicker, Select as AppSelect } from '../../components/UI';

// HR's drop-downs and date fields use the app's generic controls: the modal picker (Select) and the
// react-datepicker calendar (DatePicker) that Requisition, Payment Voucher and the rest already use.
// Both take the same props as a native <select> / <input type="date">, so the HR screens keep their
// `<option>` children and `onChange={e => … e.target.value}` handlers unchanged. Both open on <body> (`portal`): the
// Stephanet-style screens animate their containers, which would otherwise trap the picker or clip the calendar under a table.

// <option> children (arrays and fragments included) -> [{ value, label }]
function optionsFrom(children) {
  const out = [];
  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) { out.push(...optionsFrom(child.props.children)); return; }
    if (child.type !== 'option') return;
    const label = React.Children.toArray(child.props.children).join('');
    out.push({ value: child.props.value !== undefined ? String(child.props.value) : label, label });
  });
  return out;
}

const changeEvent = value => ({ target: { value }, currentTarget: { value } });

// keeps the browser's own "please fill in this field" check inside forms that rely on `required`
function RequiredGuard({ value }) {
  return (
    <input tabIndex={-1} aria-hidden="true" required value={value ?? ''} onChange={() => {}}
           style={{ position: 'absolute', left: '50%', bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }} />
  );
}

export function Select({ value, onChange, children, disabled, required, title, className, style }) {
  const options = optionsFrom(children);
  const blank = options.find(o => o.value === '');
  return (
    <div className={className} style={{ position: 'relative', minWidth: 0, ...style }}>
      <AppSelect portal value={value ?? ''} options={options} disabled={disabled}
                 title={title || blank?.label?.replace(/^—\s*|\s*—$/g, '') || 'Select option'}
                 onChange={v => onChange && onChange(changeEvent(v))} />
      {required && <RequiredGuard value={value} />}
    </div>
  );
}

// value and onChange use 'YYYY-MM-DD', as the native date input does
export function DateInput({ value, onChange, disabled, required, className, style }) {
  return (
    <div className={className} style={{ position: 'relative', minWidth: 0, ...style }}>
      <AppDatePicker portal value={value ? String(value).slice(0, 10) : ''} disabled={disabled}
                     onChange={v => onChange && onChange(changeEvent(v))} />
      {required && <RequiredGuard value={value} />}
    </div>
  );
}

// value and onChange use 'YYYY-MM', as the native month input does (the app's date picker in month mode)
export function MonthInput({ value, onChange, disabled, required, className, style }) {
  return (
    <div className={className} style={{ position: 'relative', minWidth: 0, ...style }}>
      <AppDatePicker portal month value={value ? String(value).slice(0, 7) : ''} disabled={disabled}
                     onChange={v => onChange && onChange(changeEvent(v))} />
      {required && <RequiredGuard value={value} />}
    </div>
  );
}
